/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { assertUnreachable } from '@jira/polaris__lib-ts-utils/src/index.tsx';
import { isPermissionError } from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
import { isValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import type { UFOExperience } from '@atlassian/ufo';
import {
	isExperienceMetadata,
	type CloseExperienceFunction,
	type ExperienceErrorReason,
	type ExperienceMetadata,
	type ExperienceMetadataExtra,
	type JPDExperience,
	type MarkedJPDExperience,
	type Marks,
} from './ExperienceMetadataExtra.types.tsx';

type FormattedErrorResponse = {
	message?: string;
	traceId?: string;
};

const formatUnknownError = (error: unknown): string => {
	if (typeof error === 'object') {
		try {
			return JSON.stringify(error);
		} catch {
			return String(error);
		}
	}

	return String(error);
};

export const createMarkedJpdExperience = <TMarks extends Marks>(
	ufoExperience: UFOExperience,
	marks: TMarks,
): MarkedJPDExperience<TMarks> => ({
	...createJpdExperience(ufoExperience),
	marks,
});

export const formatErrorForExperience = (error: Error): FormattedErrorResponse => {
	const errorStatus = 'statusCode' in error && error.statusCode ? `[${error.statusCode}] ` : '';
	const errorName = 'name' in error && error.name ? `${error.name}` : '';
	const errorMessage = 'message' in error ? ` - ${error.message}` : '';

	const traceId =
		typeof error === 'object' && 'traceId' in error ? String(error.traceId) : undefined;

	const message =
		`${errorStatus}${errorName}${errorMessage}` || `Unknown error: ${formatUnknownError(error)}`;

	return {
		message, // example: "[404] FetchError - Not Found"
		traceId,
	};
};

const handleReason = (
	handler: CloseExperienceFunction,
	errorReason: ExperienceErrorReason,
	extraMetadata?: ExperienceMetadataExtra,
) => {
	if (errorReason === undefined) {
		handler(extraMetadata);
		return;
	}

	if (typeof errorReason === 'string') {
		handler({
			metadata: {
				error: errorReason,
				...extraMetadata?.metadata,
			},
		});
		return;
	}

	if (errorReason instanceof Error) {
		const { message, traceId } = formatErrorForExperience(errorReason);

		return handler({
			metadata: {
				error: message,
				errors:
					'errors' in errorReason && Array.isArray(errorReason.errors)
						? errorReason.errors
								.map((err) => {
									if (typeof err === 'string') {
										return err;
									}
									if (typeof err === 'object' && err !== null && 'error' in err) {
										return err.error;
									}
									return '';
								})
								.join(', ')
						: undefined,
				traceId,
				...extraMetadata?.metadata,
			},
		});
	}

	if (isExperienceMetadata(errorReason)) {
		handler(errorReason);
		return;
	}

	assertUnreachable(errorReason);
};

export const createJpdExperience = <
	ErrorReason extends ExperienceErrorReason = ExperienceErrorReason,
	ExtraMetadata extends ExperienceMetadataExtra = ExperienceMetadataExtra,
>(
	ufoExperience: UFOExperience,
): JPDExperience<ErrorReason, ExtraMetadata> => ({
	ufoExperience,
	start: (startTime) => {
		ufoExperience.start();
		// hack to set custom start time
		if (startTime !== undefined) {
			Object.assign(ufoExperience.metrics, {
				...ufoExperience.metrics,
				startTime,
			});
		}
	},
	mark: (mark: string) => {
		ufoExperience.mark(mark);
	},
	success: (meta, endTime) => {
		ufoExperience.success(meta);
		// hack to set custom end time
		if (endTime !== undefined) {
			Object.assign(ufoExperience.metrics, {
				...ufoExperience.metrics,
				endTime,
			});
		}
	},
	successWithReason: (reason?: ErrorReason) => {
		handleReason((meta) => ufoExperience.success(meta), reason);
	},
	failure: (reason?: ErrorReason, extraMetadata?: ExtraMetadata) => {
		handleReason((meta) => ufoExperience.failure(meta), reason, extraMetadata);
	},
	abort: (reason, extraMetadata?: ExtraMetadata) => {
		handleReason((meta) => ufoExperience.abort(meta), reason, extraMetadata);
	},
});

export const wrapPromiseWithExperience = <R,>(
	promise: Promise<R>,
	experience: JPDExperience,
	errorAnalyticsId?: string,
	isExperienceErrorAborted?: (err: Error) => boolean,
): Promise<R> => {
	experience.start();
	return promise
		.then((response) => {
			experience.success();
			return response;
		})
		.catch((error) => {
			if (errorAnalyticsId) {
				fireErrorAnalytics({
					meta: {
						id: errorAnalyticsId,
						teamName: 'polaris',
					},
					// oxlint-disable-next-line typescript/consistent-type-assertions
					error: error as Error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}

			if (isClientFetchError(error)) {
				experience.abort(error);
				throw error;
			}

			if (isValidationError(error)) {
				experience.abort(error);
				throw error;
			}

			if (isExperienceErrorAborted && isExperienceErrorAborted(error)) {
				experience.abort(error);
				throw error;
			}

			experience.failure(error);
			throw error;
		});
};

export const wrapWithExperience = <R,>(
	experience: JPDExperience,
	callback: () => Promise<R>,
	options?: {
		ignorePermissionError?: boolean;
		getMetadata?: (result: R) => ExperienceMetadataExtra['metadata'];
	},
): Promise<R> => {
	experience.start();
	return callback()
		.then((result) => {
			experience.success(
				options?.getMetadata ? { metadata: options.getMetadata(result) } : undefined,
			);
			return result;
		})
		.catch((error) => {
			if (isClientFetchError(error)) {
				// oxlint-disable-next-line typescript/consistent-type-assertions
				experience.abort(error as Error);
				throw error;
			}

			if (options?.ignorePermissionError && isPermissionError(error)) {
				experience.abort(error);
				throw error;
			}

			if (error instanceof Error) {
				experience.failure(error);
			}
			throw error;
		});
};

export const createExperienceWithMetaType = (
	experience: JPDExperience,
	metaType: string,
	parseMetadataError: (error?: Error) => string,
): {
	ufoExperience: UFOExperience;
	start: (startTime?: number) => void;
	mark: (mark: string) => void;
	success: (meta?: ExperienceMetadata, endTime?: number) => void;
	successWithReason: (error?: Error) => void;
	failure: (error?: Error) => void;
	abort: (error?: Error) => void;
} => ({
	ufoExperience: experience.ufoExperience,
	start: experience.start,
	mark: experience.mark,
	success: (meta?: ExperienceMetadata, endTime?: number): void => {
		experience.success(
			{
				metadata: {
					type: metaType,
				},
				...meta,
			},
			endTime,
		);
	},
	successWithReason: (error?: Error): void => {
		experience.successWithReason({
			metadata: {
				error: parseMetadataError(error),
				type: metaType,
			},
		});
	},
	failure: (error?: Error): void => {
		if (error && isClientFetchError(error)) {
			experience.abort({
				metadata: {
					error: parseMetadataError(error),
					type: metaType,
				},
			});
		} else {
			experience.failure({
				metadata: {
					error: parseMetadataError(error),
					type: metaType,
				},
			});
		}
	},
	abort: (error?: Error): void => {
		experience.abort({
			metadata: {
				error: parseMetadataError(error),
				type: metaType,
			},
		});
	},
});
